import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axiosInstance from "../../../axios-instance";
import { getAdminBusinessId } from "../../../utils/authHelper";

/* ─── Design tokens (MeetOcto light theme) ───────────────────────────── */
const C = {
  orange: "#FE9920",
  orangeLight: "#FFF7ED",
  dark: "#111827",
  mid: "#374151",
  muted: "#6B7280",
  border: "#E5E7EB",
  green: "#10B981",
  greenBg: "#F0FDF4",
  greenBorder: "#BBF7D0",
  greenDark: "#065F46",
  surface: "#F9FAFB",
  danger: "#EF4444",
  dangerBg: "#FEF2F2",
  dangerBorder: "#FECACA",
  amber: "#F59E0B",
  amberBg: "#FFFBEB",
  amberBorder: "#FDE68A",
  amberDark: "#92400E",
};

const PAGE_SIZE = 10;

const S = {
  wrapper: { padding: "24px" },
  section: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "24px",
    marginBottom: 24,
  },
  sectionTitle: { fontSize: 17, fontWeight: 700, color: C.dark, marginBottom: 6 },
  sectionSub: { fontSize: 13, color: C.muted, marginBottom: 16, lineHeight: 1.6 },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 16,
    alignItems: "center",
  },
  searchInput: {
    flex: "1 1 240px",
    padding: "10px 14px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    fontSize: 13,
    color: C.dark,
    outline: "none",
    minWidth: 0,
  },
  select: {
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    fontSize: 13,
    color: C.mid,
    background: "#fff",
    cursor: "pointer",
    outline: "none",
  },
  btnPrimary: {
    padding: "10px 18px",
    borderRadius: 9,
    border: "none",
    background: C.orange,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnSecondary: {
    padding: "10px 16px",
    borderRadius: 9,
    border: `1.5px solid ${C.border}`,
    background: "#fff",
    color: C.mid,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  countBadge: {
    fontSize: 12,
    color: C.muted,
    background: C.surface,
    padding: "5px 10px",
    borderRadius: 6,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  warningBanner: {
    background: C.amberBg,
    border: `1px solid ${C.amberBorder}`,
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: 16,
    fontSize: 13,
    color: C.amberDark,
    lineHeight: 1.5,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    background: C.surface,
    fontSize: 11,
    fontWeight: 700,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    borderBottom: `2px solid ${C.border}`,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px",
    borderBottom: `1px solid #F3F4F6`,
    color: C.mid,
    verticalAlign: "middle",
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "8px 0 10px",
  },
  toggleBtn: (active, disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 14px",
    borderRadius: 9999,
    fontSize: 12,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    border: "none",
    background: disabled ? "#F3F4F6" : active ? C.greenBg : C.dangerBg,
    color: disabled ? C.muted : active ? C.greenDark : "#991B1B",
    opacity: disabled ? 0.65 : 1,
  }),
  dot: (active) => ({
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: active ? C.green : C.danger,
    display: "inline-block",
  }),
  blockedTag: {
    display: "inline-block",
    background: "#FEE2E2",
    color: "#991B1B",
    borderRadius: 6,
    padding: "2px 8px",
    fontSize: 11,
    fontWeight: 600,
  },
  empty: { textAlign: "center", padding: "36px 0", color: C.muted, fontSize: 14 },
  spinner: { textAlign: "center", padding: "36px 0", color: C.muted, fontSize: 14 },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 16,
  },
  pageBtn: (disabled) => ({
    padding: "7px 12px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: disabled ? C.surface : "#fff",
    color: disabled ? "#9CA3AF" : C.mid,
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
  }),
  // Modal
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(17, 24, 39, 0.45)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    width: "100%",
    maxWidth: 720,
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 50px rgba(17, 24, 39, 0.2)",
  },
  modalHeader: {
    padding: "20px 22px 12px",
    borderBottom: `1px solid ${C.border}`,
  },
  modalBody: {
    padding: "16px 22px",
    overflowY: "auto",
    flex: 1,
  },
  modalFooter: {
    padding: "14px 22px",
    borderTop: `1px solid ${C.border}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
};

function formatExample(credits) {
  const n = Number(credits) || 0;
  if (n <= 0) return "—";
  const msgs = Math.floor(250 / n);
  return `250 credits ≈ ${msgs} SMS`;
}

function AddCountriesModal({ open, onClose, businessId, onAdded, showToast }) {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const searchRef = useRef(null);

  const loadCatalog = useCallback(() => {
    if (!businessId || !open) return;
    setLoading(true);
    setError(null);
    axiosInstance
      .get("admin/sms-countries/catalog", {
        params: { business_id: businessId, search },
      })
      .then((res) => setCountries(res.data?.countries || []))
      .catch(() => setError("Failed to load countries. Please try again."))
      .finally(() => setLoading(false));
  }, [businessId, open, search]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(loadCatalog, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [open, loadCatalog, search]);

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setSearch("");
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  const expensiveSelected = useMemo(() => {
    return countries.filter(
      (c) => selected.has(c.country_id) && Number(c.credits_per_segment) > 1
    );
  }, [countries, selected]);

  const toggleOne = (id, selectable) => {
    if (!selectable) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (!selected.size) return;
    setSaving(true);
    try {
      const res = await axiosInstance.post("admin/sms-countries/add", {
        business_id: businessId,
        country_ids: Array.from(selected),
      });
      showToast(res.data?.message || "Countries added", "green");
      onAdded();
      onClose();
    } catch (e) {
      showToast(e?.response?.data?.error || "Failed to add countries.", "red");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      style={S.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
      role="presentation"
    >
      <div style={S.modal} role="dialog" aria-modal="true" aria-labelledby="add-countries-title">
        <div style={S.modalHeader}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            <div>
              <h3 id="add-countries-title" style={{ ...S.sectionTitle, marginBottom: 8 }}>
                Add countries
              </h3>
              <p style={{ ...S.sectionSub, marginBottom: 0 }}>
                Select the countries where your organization needs to send SMS reminders. The
                required credits depend on the destination and message length.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              aria-label="Close"
              style={{
                border: "none",
                background: "transparent",
                fontSize: 20,
                lineHeight: 1,
                color: C.muted,
                cursor: "pointer",
                padding: 4,
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={S.modalBody}>
          <input
            ref={searchRef}
            type="text"
            placeholder="Search by country name or calling code…"
            style={{ ...S.searchInput, width: "100%", flex: "none", marginBottom: 12 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {expensiveSelected.length > 0 && (
            <div style={S.warningBanner}>
              <strong>Higher credit cost:</strong> You selected destination
              {expensiveSelected.length > 1 ? "s" : ""} that cost more than 1 credit per SMS
              segment
              {expensiveSelected.length <= 3
                ? ` (${expensiveSelected.map((c) => c.name).join(", ")})`
                : ""}.
              Credits are deducted per segment; long messages use multiple segments.
            </div>
          )}

          {loading ? (
            <div style={S.spinner}>Loading countries…</div>
          ) : error ? (
            <div style={{ ...S.empty, color: C.danger }}>{error}</div>
          ) : countries.length === 0 ? (
            <div style={S.empty}>
              {search
                ? "No matching countries available to add."
                : "All destinations are already on your list."}
            </div>
          ) : (
            <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 10 }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Select</th>
                    <th style={S.th}>Destination</th>
                    <th style={S.th}>Credits per SMS</th>
                    <th style={S.th}>Example</th>
                  </tr>
                </thead>
                <tbody>
                  {countries.map((c) => {
                    const selectable = !!c.is_selectable && !c.is_blocked;
                    const checked = selected.has(c.country_id);
                    return (
                      <tr
                        key={c.country_id}
                        style={{
                          opacity: selectable ? 1 : 0.55,
                          background: checked ? C.orangeLight : undefined,
                        }}
                      >
                        <td style={S.td}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!selectable}
                            onChange={() => toggleOne(c.country_id, selectable)}
                            aria-label={`Select ${c.name}`}
                          />
                        </td>
                        <td style={S.td}>
                          <span style={{ fontWeight: 600, color: C.dark }}>
                            {(c.iso_code || "").toUpperCase()} {c.name}
                          </span>
                          <span style={{ color: C.muted }}> · +{c.calling_code}</span>
                          {c.is_blocked && (
                            <span style={{ ...S.blockedTag, marginLeft: 8 }}>Unavailable</span>
                          )}
                        </td>
                        <td style={S.td}>
                          <strong style={{ color: C.dark }}>{c.credits_per_segment}</strong>{" "}
                          credit{c.credits_per_segment === 1 ? "" : "s"}
                        </td>
                        <td style={S.td}>{formatExample(c.credits_per_segment)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={S.modalFooter}>
          <span style={{ fontSize: 13, color: C.muted }}>
            {selected.size} selected · Added countries start as <strong>Inactive</strong>
          </span>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" style={S.btnSecondary} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              style={{
                ...S.btnPrimary,
                opacity: !selected.size || saving ? 0.6 : 1,
                cursor: !selected.size || saving ? "not-allowed" : "pointer",
              }}
              onClick={handleAdd}
              disabled={!selected.size || saving}
            >
              {saving ? "Adding…" : "Add countries"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SMSDestinationsTab() {
  const businessId = getAdminBusinessId();

  const [countries, setCountries] = useState([]);
  const [counts, setCounts] = useState({ enabled: 0, total_added: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [toggling, setToggling] = useState({});
  const [toast, setToast] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [contextWarning, setContextWarning] = useState(null);

  const showToast = useCallback((msg, color) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const load = useCallback(() => {
    if (!businessId) return;
    setLoading(true);
    setError(null);
    axiosInstance
      .get("admin/sms-countries", {
        params: {
          business_id: businessId,
          search,
          filter: statusFilter,
        },
      })
      .then((res) => {
        setCountries(res.data?.countries || []);
        setCounts(res.data?.counts || { enabled: 0, total_added: 0 });
        setPage(1);
      })
      .catch(() => setError("Failed to load destinations. Please try again."))
      .finally(() => setLoading(false));
  }, [businessId, search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const enabledCountries = useMemo(
    () => countries.filter((c) => c.is_org_enabled && !c.is_blocked),
    [countries]
  );
  const otherCountries = useMemo(
    () => countries.filter((c) => !(c.is_org_enabled && !c.is_blocked)),
    [countries]
  );

  // Paginate the combined list while keeping Enabled section first in the data order from API.
  // For UI: show Enabled block (all enabled on current filter page slice) — simpler approach:
  // paginate full sorted list; if filter is all, render enabled first then others without splitting pages awkwardly.
  const ordered = useMemo(() => {
    if (statusFilter === "enabled") return enabledCountries;
    if (statusFilter === "available" || statusFilter === "unavailable") return countries;
    return [...enabledCountries, ...otherCountries];
  }, [statusFilter, enabledCountries, otherCountries, countries]);

  const totalPages = Math.max(1, Math.ceil(ordered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = ordered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const pageEnabled = pageRows.filter((c) => c.is_org_enabled && !c.is_blocked);
  const pageOthers = pageRows.filter((c) => !(c.is_org_enabled && !c.is_blocked));

  const handleToggle = async (country, currentlyEnabled) => {
    if (country.is_blocked) return;
    const countryId = country.country_id;
    setToggling((prev) => ({ ...prev, [countryId]: true }));
    try {
      const res = await axiosInstance.post("admin/sms-countries/toggle", {
        business_id: businessId,
        country_id: countryId,
        is_enabled: !currentlyEnabled,
      });
      const nowEnabled = !currentlyEnabled;

      // Keep list consistent with the active filter (e.g. enabling while on Available).
      const stillMatchesFilter =
        statusFilter === "all" ||
        (statusFilter === "enabled" && nowEnabled) ||
        (statusFilter === "available" && !nowEnabled) ||
        (statusFilter === "unavailable" && !!country.is_blocked);

      if (stillMatchesFilter) {
        setCountries((prev) =>
          prev.map((c) =>
            c.country_id === countryId ? { ...c, is_org_enabled: nowEnabled } : c
          )
        );
      } else {
        setCountries((prev) => prev.filter((c) => c.country_id !== countryId));
      }

      setCounts((prev) => ({
        ...prev,
        enabled: Math.max(0, (prev.enabled || 0) + (nowEnabled ? 1 : -1)),
      }));
      if (nowEnabled) {
        showToast("Destination activated ✓", "green");
        if (res.data?.expensive || Number(country.credits_per_segment) > 1) {
          setContextWarning(
            `${country.name} costs ${country.credits_per_segment} credits per SMS segment. SMS reminders will only send here while this destination stays Active.`
          );
        } else {
          setContextWarning(null);
        }
      } else {
        showToast("Destination deactivated", "red");
        setContextWarning(null);
      }
    } catch (e) {
      showToast(e?.response?.data?.error || "Failed to update. Please try again.", "red");
    } finally {
      setToggling((prev) => ({ ...prev, [countryId]: false }));
    }
  };

  const renderRows = (rows) =>
    rows.map((c) => {
      const isActive = !!c.is_org_enabled;
      const isBlocked = !!c.is_blocked;
      const isToggling = !!toggling[c.country_id];

      return (
        <tr key={c.country_id}>
          <td style={S.td}>
            <span style={{ fontWeight: 600, color: C.dark }}>{c.name}</span>
            {isBlocked && <span style={{ ...S.blockedTag, marginLeft: 8 }}>Unavailable</span>}
          </td>
          <td style={S.td}>+{c.calling_code}</td>
          <td style={S.td}>
            <strong style={{ color: C.dark }}>{c.credits_per_segment}</strong>{" "}
            credit{Number(c.credits_per_segment) === 1 ? "" : "s"}
          </td>
          <td style={S.td}>{formatExample(c.credits_per_segment)}</td>
          <td style={S.td}>
            {isBlocked ? (
              <span style={S.blockedTag}>Unavailable</span>
            ) : (
              <button
                type="button"
                style={S.toggleBtn(isActive, isToggling)}
                onClick={() => handleToggle(c, isActive)}
                disabled={isToggling}
                title={
                  isActive
                    ? "Click to deactivate — SMS will not send to this country"
                    : "Click to activate — SMS can send to this country"
                }
              >
                <span style={S.dot(isActive)} />
                {isToggling ? "Saving…" : isActive ? "Enabled" : "Enable"}
              </button>
            )}
          </td>
        </tr>
      );
    });

  return (
    <div style={S.wrapper}>
      {toast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 10001,
            background: toast.color === "green" ? C.greenBg : C.dangerBg,
            border: `1px solid ${toast.color === "green" ? C.greenBorder : C.dangerBorder}`,
            color: toast.color === "green" ? C.greenDark : "#991B1B",
            borderRadius: 10,
            padding: "12px 20px",
            fontSize: 14,
            fontWeight: 600,
            boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
          }}
        >
          {toast.msg}
        </div>
      )}

      <div style={S.section}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 8,
          }}
        >
          <div>
            <h2 style={S.sectionTitle}>SMS Countries</h2>
            <p style={{ ...S.sectionSub, marginBottom: 0 }}>
              Manage where your organization can send SMS reminders. Only{" "}
              <strong>Enabled</strong> destinations can receive SMS. Credits depend on destination
              and message length.
            </p>
          </div>
          <button type="button" style={S.btnPrimary} onClick={() => setModalOpen(true)}>
            Add countries
          </button>
        </div>

        <div style={{ ...S.countBadge, display: "inline-block", marginBottom: 14 }}>
          {counts.enabled} enabled · {counts.total_added} on your list
        </div>

        {contextWarning && (
          <div style={S.warningBanner}>
            <strong>Note:</strong> {contextWarning}
            <button
              type="button"
              onClick={() => setContextWarning(null)}
              style={{
                marginLeft: 12,
                border: "none",
                background: "transparent",
                color: C.amberDark,
                fontWeight: 700,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        <div style={S.controls}>
          <input
            type="text"
            placeholder="Search country or calling code…"
            style={S.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            style={S.select}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All</option>
            <option value="enabled">Enabled</option>
            <option value="available">Available</option>
            <option value="unavailable">Unavailable</option>
          </select>
        </div>

        {loading ? (
          <div style={S.spinner}>Loading destinations…</div>
        ) : error ? (
          <div style={{ ...S.empty, color: C.danger }}>
            {error}
            <br />
            <button
              type="button"
              onClick={load}
              style={{ ...S.btnPrimary, marginTop: 12 }}
            >
              Retry
            </button>
          </div>
        ) : ordered.length === 0 ? (
          <div style={S.empty}>
            {search || statusFilter !== "all"
              ? "No destinations match your filters."
              : "No countries on your list yet. Click Add countries to get started."}
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 10 }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Country</th>
                    <th style={S.th}>Calling code</th>
                    <th style={S.th}>Credits per SMS</th>
                    <th style={S.th}>Example</th>
                    <th style={S.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {statusFilter === "all" && pageEnabled.length > 0 && (
                    <>
                      <tr>
                        <td colSpan={5} style={{ ...S.td, background: C.surface, borderBottom: `1px solid ${C.border}` }}>
                          <span style={S.sectionLabel}>Enabled countries</span>
                        </td>
                      </tr>
                      {renderRows(pageEnabled)}
                    </>
                  )}
                  {statusFilter === "all" && pageOthers.length > 0 && (
                    <>
                      {pageEnabled.length > 0 && (
                        <tr>
                          <td colSpan={5} style={{ ...S.td, background: C.surface, borderBottom: `1px solid ${C.border}` }}>
                            <span style={S.sectionLabel}>Other countries on your list</span>
                          </td>
                        </tr>
                      )}
                      {renderRows(pageOthers)}
                    </>
                  )}
                  {statusFilter !== "all" && renderRows(pageRows)}
                </tbody>
              </table>
            </div>

            <div style={S.pagination}>
              <span style={{ fontSize: 13, color: C.muted }}>
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                {Math.min(currentPage * PAGE_SIZE, ordered.length)} of {ordered.length}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  style={S.pageBtn(currentPage <= 1)}
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span style={{ fontSize: 13, color: C.mid, alignSelf: "center" }}>
                  Page {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  style={S.pageBtn(currentPage >= totalPages)}
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        <p style={{ fontSize: 11, color: C.muted, marginTop: 16, lineHeight: 1.55, marginBottom: 0 }}>
          Estimates assume 250 personal credits and 1 segment per message. Actual usage varies with
          message length, encoding, and carrier fees.
        </p>
      </div>

      <AddCountriesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        businessId={businessId}
        onAdded={load}
        showToast={showToast}
      />
    </div>
  );
}
